import { Component, Input, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { Map, NavigationControl } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-mapbox-heatmap',
  templateUrl: './app-mapbox-heatmap.component.html',
  styleUrls: ['./app-mapbox-heatmap.component.scss'],
})
export class MapboxHeatmapComponent implements AfterViewInit, OnChanges {
  @Input() searchPoints: any[] = [];
  @Input() directionPoints: any[] = [];
  @Input() center: [number, number] = [-87.6500523, 41.850033]; // Chicago, US center
  @Input() zoom = 4;
  @Input() mapId = 'map';
  @Input() isLoading = false;
  @Input() selectedLocation: any;
  private selectedMarker?: mapboxgl.Marker;
  private addSelectedMarker(): void {
    const selected = this.selectedLocation;
    const lat = Number(selected?.latitude);
    const lng = Number(selected?.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      const lngLat: [number, number] = [lng, lat];

      if (this.selectedMarker) {
        this.selectedMarker.remove();
      }

      this.selectedMarker = new mapboxgl.Marker({ color: 'red' })
        .setLngLat(lngLat)
        .addTo(this.map);
    }
  }

  map!: Map;

  ngAfterViewInit() {
    mapboxgl.accessToken = environment.mapbox.accessToken;
    const centerToUse: [number, number] =
      this.searchPoints.length > 0 ? [+this.searchPoints[0].longitude, +this.searchPoints[0].latitude] : this.center;
    this.map = new mapboxgl.Map({
      container: this.mapId,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-97.167524, 40.044438], // starting position [lng, lat]
      zoom: 3,
    });

    this.map.addControl(new NavigationControl());

    this.map.on('load', () => {
      this.updateHeatmapLayer();
      // ✅ Add marker on initial load if selectedLocation is already available
      if (this.selectedLocation) {
        this.addSelectedMarker();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.map && this.map.isStyleLoaded()) {
      this.updateHeatmapLayer();

      if (changes['selectedLocation'] && this.selectedLocation) {
        this.addSelectedMarker();
      }
    }
  }

  private updateHeatmapLayer(): void {
    console.log('Updating heatmap layer with search points:', this.searchPoints);
    const features: GeoJSON.Feature<GeoJSON.Point>[] = this.searchPoints.map((p) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [+p.longitude, +p.latitude],
      },
      properties: {
        score: isNaN(Number(p.score)) ? 0 : Number(p.score),
      },
    }));

    const source = this.map.getSource('heat') as mapboxgl.GeoJSONSource;

    if (source) {
      source.setData({
        type: 'FeatureCollection',
        features,
      });
    } else {
      this.map.addSource('heat', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features,
        },
      });

      this.map.addLayer({
        id: 'heatmap',
        type: 'heatmap',
        source: 'heat',
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'score'], 0, 0, 10, 1],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 9, 2],
          'heatmap-radius': 45,
          'heatmap-opacity': 0.8,
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0,
            'rgba(0, 0, 255, 0)',
            0.1,
            'blue',
            0.2,
            'cyan',
            0.4,
            'lime',
            0.6,
            'yellow',
            0.8,
            'orange',
            1,
            'red',
          ],
        },
      });
    }

    if (features.length > 0) {
      const bounds = this.getBoundingBox(this.searchPoints);
      this.map.fitBounds(bounds, { padding: 40, zoom: 8 });
    }
  }

  private getBoundingBox(points: any[]): [[number, number], [number, number]] {
    const lats = points.map((p) => +p.latitude);
    const lngs = points.map((p) => +p.longitude);
    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];
  }
}
